import React from 'react'
import { Navigate } from 'react-router-dom'
import { useSelector } from 'react-redux'

import LoadingSpinner from 'components/LoadingSpinner'
import { useCurrentPractice } from 'contexts/PracticeContext'
import { selectContext } from 'selectors/auth'
import { useConsolidatorFeatureFlag } from 'hooks/useConsolidatorFeatureFlag'

interface ConsolidatorFeatureFlagGuardProps {
	/** Feature flag key to check on the consolidator (e.g. `enableRebatePayoutsPage`). */
	name: string
	/** Require the user to be a consolidator admin. Defaults to true; covers the existing rebate-history pages. */
	requireAdmin?: boolean
	children: React.ReactNode
}

/**
 * Generic route guard that gates children behind a consolidator feature flag resolved at runtime
 * via the `consolidatorFeatureFlag` GraphQL query. Replaces page-specific guards (e.g.
 * EnterpriseRebateHistoryGuard) so each page can be toggled independently per consolidator.
 */
const ConsolidatorFeatureFlagGuard: React.FC<ConsolidatorFeatureFlagGuardProps> = ({
	name,
	requireAdmin = true,
	children
}) => {
	const { currentPracticeObject } = useCurrentPractice()
	const context = useSelector(selectContext)

	const hasPractice = Boolean(currentPracticeObject?.id)
	const { enabled, loading } = useConsolidatorFeatureFlag(name, { pause: !hasPractice })

	if (!hasPractice || loading) {
		return <LoadingSpinner />
	}

	const isAdmin = context.user?.isAdmin === true
	if (requireAdmin && !isAdmin) {
		return <Navigate to="/dashboard" />
	}

	if (!enabled) {
		return <Navigate to="/dashboard" />
	}

	return <>{children}</>
}

export default ConsolidatorFeatureFlagGuard
